/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Briefcase,
  Car,
  MessageCircle,
  Plus,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCircle,
  XCircle,
  RotateCcw,
  Pencil,
} from 'lucide-react';
import NoDataHandling from 'components/shared/NoData/NoDataHandling';
import SingleViewLayout from 'layouts/SingleViewLayout';
import { useParams, useNavigate } from 'react-router-dom';
import { getBackgroundColor } from 'utils/Functions';
import useGetTrailLogs from 'hooks/fines/useGetFineTrailLogs';
import moment from 'moment';
import FineDetailsSkeleton from 'components/common/skeletons/FineDetailsSkeleton';
import DialogLayout from 'layouts/DialogLayout';
import MultiSelect from 'components/fines/MultiSelectComponent';
import useAddInfraction from 'hooks/fines/useAddInfraction';
import { useAuth } from 'context/AuthContext';
import { Bounce, toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import ZoomableImage from 'components/common/ZoomableImage';
import { useFines } from 'context/FinesContext';
import useOfficerNotifyFine from 'hooks/fines/useOfficerNotifyFine';
import useGetFineByTicketNumber from 'hooks/fines/useGetFineByTicketNumber';
import useAcceptCaseCheckerFine from 'hooks/fines/useAcceptCaseCheckerFine';
import useRejectCaseCheckerFine from 'hooks/fines/useRejectCaseCheckerFine';
import useCorrectCaseCheckerPlate from 'hooks/fines/useCorrectCaseCheckerPlate';
import useRecallFine from 'hooks/fines/useRecallFine';
import useGetSmsTemplates from 'hooks/sms/useGetSmsTemplates';
import { SmsTemplate } from 'utils/types';

interface InfoItemProps {
  label: string;
  value?: string | number;
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <div className="space-y-1">
    <p className="text-sm text-gray-500">{label}</p>
    <p className="font-medium">{value}</p>
  </div>
);

const EditableInfoItem: React.FC<
  InfoItemProps & {
    onEdit?: () => void;
    editLabel?: string;
    isEditingDisabled?: boolean;
  }
> = ({ label, value, onEdit, editLabel, isEditingDisabled }) => (
  <div className="space-y-1">
    <p className="text-sm text-gray-500">{label}</p>
    <div className="flex items-center gap-2">
      <p className="font-medium">{value}</p>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          disabled={isEditingDisabled}
          aria-label={editLabel ?? `Edit ${label}`}
          className="inline-flex items-center justify-center rounded-md p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}
    </div>
  </div>
);

const SmsConfirmModal = ({
  onConfirm,
  onCancel,
  isSending,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  isSending: boolean;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <button
      type="button"
      aria-label="Close send SMS confirmation"
      className="absolute inset-0 bg-black/40"
      onClick={onCancel}
      disabled={isSending}
    />
    <div className="relative bg-white rounded-xl shadow-xl p-6 w-80 flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-gray-800">
          Send SMS Notification?
        </h2>
        <p className="text-sm text-gray-500">
          This will send an SMS notification to the vehicle owner about this
          fine. Are you sure you want to proceed?
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={isSending}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isSending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSending ? (
            <>
              <svg
                className="animate-spin h-3 w-3 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Sending…
            </>
          ) : (
            <>
              <Send className="h-3 w-3" />
              Send SMS
            </>
          )}
        </button>
      </div>
    </div>
  </div>
);

const REJECTION_REASONS = [
  'Plate does not match violation image',
  'Vehicle details do not match case data',
  'Violation image is unclear',
  'Duplicate case',
  'Invalid or incomplete violation data',
  'No violation visible in image',
] as const;

type RecallMode = 'default' | 'template' | 'override';

const RECALL_REASONS = [
  'Plate misread by ANPR',
  'Officer mis-keyed the offence code',
  'Custom one-off message needed',
  'Wrong vehicle registration number',
  'Violation details require correction',
] as const;

const AcceptCaseModal = ({
  onConfirm,
  onCancel,
  isAccepting,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  isAccepting: boolean;
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <button
      type="button"
      aria-label="Close accept case confirmation"
      className="absolute inset-0 bg-black/40"
      onClick={onCancel}
      disabled={isAccepting}
    />
    <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-gray-800">Accept Case?</h2>
        <p className="text-sm text-gray-500">
          This will accept the fine and queue it for officer action. Are you
          sure you want to proceed?
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={isAccepting}
          className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isAccepting}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CheckCircle className="h-4 w-4" />
          {isAccepting ? 'Accepting...' : 'Accept Case'}
        </button>
      </div>
    </div>
  </div>
);

const RejectCaseModal = ({
  reason,
  onReasonChange,
  onConfirm,
  onCancel,
  isRejecting,
}: {
  reason: string;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isRejecting: boolean;
}) => {
  const trimmedReason = reason.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close reject case confirmation"
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
        disabled={isRejecting}
      />
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-lg flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-gray-800">
            Reject Case?
          </h2>
          <p className="text-sm text-gray-500">
            Provide the reason this fine should be rejected. This will be logged
            against the case.
          </p>
        </div>
        <div>
          <label
            htmlFor="case-rejection-reason"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Rejection reason
          </label>
          <select
            id="case-rejection-reason"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            disabled={isRejecting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-red-500 focus:border-red-500 text-gray-700 disabled:opacity-50"
          >
            <option value="">Select a rejection reason</option>
            {REJECTION_REASONS.map((rejectionReason) => (
              <option key={rejectionReason} value={rejectionReason}>
                {rejectionReason}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isRejecting}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isRejecting || trimmedReason.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle className="h-4 w-4" />
            {isRejecting ? 'Rejecting...' : 'Reject Case'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CorrectPlateModal = ({
  plateNumber,
  reason,
  onPlateNumberChange,
  onReasonChange,
  onConfirm,
  onCancel,
  isCorrecting,
}: {
  plateNumber: string;
  reason: string;
  onPlateNumberChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isCorrecting: boolean;
}) => {
  const canSubmit = plateNumber.trim().length > 0 && reason.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close correct plate modal"
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
        disabled={isCorrecting}
      />
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-lg flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-gray-800">
            Correct Number Plate
          </h2>
          <p className="text-sm text-gray-500">
            Update the vehicle registration number after confirming it against
            the violation image.
          </p>
        </div>

        <div>
          <label
            htmlFor="corrected-plate-number"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Correct plate number
          </label>
          <input
            id="corrected-plate-number"
            value={plateNumber}
            onChange={(event) =>
              onPlateNumberChange(event.target.value.toUpperCase())
            }
            disabled={isCorrecting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          />
        </div>

        <div>
          <label
            htmlFor="plate-correction-reason"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Correction reason
          </label>
          <textarea
            id="plate-correction-reason"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            disabled={isCorrecting}
            rows={4}
            placeholder="ANPR misread - KBT1610 captured, correct plate is KBT161C"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isCorrecting}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isCorrecting || !canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCorrecting ? 'Correcting...' : 'Correct Plate'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CorrectLocationModal = ({
  location,
  roadName,
  city,
  gps,
  reason,
  onLocationChange,
  onRoadNameChange,
  onCityChange,
  onGpsChange,
  onReasonChange,
  onConfirm,
  onCancel,
  isCorrecting,
}: {
  location: string;
  roadName: string;
  city: string;
  gps: string;
  reason: string;
  onLocationChange: (value: string) => void;
  onRoadNameChange: (value: string) => void;
  onCityChange: (value: string) => void;
  onGpsChange: (value: string) => void;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isCorrecting: boolean;
}) => {
  const canSubmit =
    location.trim().length > 0 &&
    roadName.trim().length > 0 &&
    city.trim().length > 0 &&
    gps.trim().length > 0 &&
    reason.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close correct location modal"
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
        disabled={isCorrecting}
      />
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-lg flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-gray-800">
            Correct Location
          </h2>
          <p className="text-sm text-gray-500">
            Update the violation location after confirming it against the
            violation image or dashcam footage.
          </p>
        </div>

        <div>
          <label
            htmlFor="corrected-location"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Location
          </label>
          <input
            id="corrected-location"
            value={location}
            onChange={(event) => onLocationChange(event.target.value)}
            disabled={isCorrecting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          />
        </div>

        <div>
          <label
            htmlFor="corrected-road-name"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Road name
          </label>
          <input
            id="corrected-road-name"
            value={roadName}
            onChange={(event) => onRoadNameChange(event.target.value)}
            disabled={isCorrecting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          />
        </div>

        <div>
          <label
            htmlFor="corrected-city"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            City
          </label>
          <input
            id="corrected-city"
            value={city}
            onChange={(event) => onCityChange(event.target.value)}
            disabled={isCorrecting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          />
        </div>

        <div>
          <label
            htmlFor="corrected-gps"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            GPS coordinates
          </label>
          <input
            id="corrected-gps"
            value={gps}
            onChange={(event) => onGpsChange(event.target.value)}
            disabled={isCorrecting}
            placeholder="-1.3032,36.7821"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          />
        </div>

        <div>
          <label
            htmlFor="location-correction-reason"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Correction reason
          </label>
          <textarea
            id="location-correction-reason"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            disabled={isCorrecting}
            rows={4}
            placeholder="Officer dictation corrected after dashcam review"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isCorrecting}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isCorrecting || !canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCorrecting ? 'Correcting...' : 'Correct Location'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RecallSmsModal = ({
  mode,
  reason,
  templateId,
  messageOverride,
  smsTemplates,
  isLoadingTemplates,
  isRecalling,
  onModeChange,
  onReasonChange,
  onTemplateChange,
  onMessageOverrideChange,
  onConfirm,
  onCancel,
}: {
  mode: RecallMode;
  reason: string;
  templateId: string;
  messageOverride: string;
  smsTemplates: SmsTemplate[];
  isLoadingTemplates: boolean;
  isRecalling: boolean;
  onModeChange: (value: RecallMode) => void;
  onReasonChange: (value: string) => void;
  onTemplateChange: (value: string) => void;
  onMessageOverrideChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const canSubmit =
    reason.trim().length > 0 &&
    (mode === 'default' ||
      (mode === 'template' && templateId.length > 0) ||
      (mode === 'override' && messageOverride.trim().length > 0));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close recall SMS modal"
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
        disabled={isRecalling}
      />
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl flex flex-col gap-4 max-h-[90vh] overflow-y-auto">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold text-gray-800">
            Recall Sent SMS
          </h2>
          <p className="text-sm text-gray-500">
            Choose how to notify the owner about this recall.
          </p>
        </div>

        <div>
          <label
            htmlFor="recall-mode"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Recall type
          </label>
          <select
            id="recall-mode"
            value={mode}
            onChange={(event) => onModeChange(event.target.value as RecallMode)}
            disabled={isRecalling}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          >
            <option value="default">Default recall</option>
            <option value="template">Recall with SMS template</option>
            <option value="override">Recall with message override</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="recall-reason"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Recall reason
          </label>
          <select
            id="recall-reason"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            disabled={isRecalling}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
          >
            <option value="">Select a recall reason</option>
            {RECALL_REASONS.map((recallReason) => (
              <option key={recallReason} value={recallReason}>
                {recallReason}
              </option>
            ))}
          </select>
        </div>

        {mode === 'template' && (
          <div>
            <label
              htmlFor="recall-template"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              SMS template
            </label>
            <select
              id="recall-template"
              value={templateId}
              onChange={(event) => onTemplateChange(event.target.value)}
              disabled={isRecalling || isLoadingTemplates}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
            >
              <option value="">
                {isLoadingTemplates
                  ? 'Loading templates...'
                  : 'Select an SMS template'}
              </option>
              {smsTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.templateType}
                  {template.offence
                    ? ` - ${template.offence.offenceLabel}`
                    : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {mode === 'override' && (
          <div>
            <label
              htmlFor="recall-message-override"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              SMS message override
            </label>
            <textarea
              id="recall-message-override"
              value={messageOverride}
              onChange={(event) => onMessageOverrideChange(event.target.value)}
              disabled={isRecalling}
              rows={5}
              placeholder="RECALL: Ticket {ticketNumber} for {vehicleReg} has been withdrawn. Reason: {recallReason}. No payment required."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700 disabled:opacity-50"
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isRecalling}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isRecalling || !canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="h-4 w-4" />
            {isRecalling ? 'Recalling...' : 'Recall SMS'}
          </button>
        </div>
      </div>
    </div>
  );
};

const FineDetails: React.FC = () => {
  const { auth } = useAuth();
  const { keycloak } = useKeycloak();
  const { id } = useParams<{ id: string }>();
  const { data: fine, isLoading: fineLoading } = useGetFineByTicketNumber(
    id || '',
  );
  const { mutateAsync, isPending } = useGetTrailLogs();
  const [trailLog, setTrailLog] = useState<any>();
  const [isOpen, setIsOpen] = useState(false);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCorrectPlateModal, setShowCorrectPlateModal] = useState(false);
  const [showCorrectLocationModal, setShowCorrectLocationModal] =
    useState(false);
  const [showRecallModal, setShowRecallModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [correctedPlateNumber, setCorrectedPlateNumber] = useState('');
  const [plateCorrectionReason, setPlateCorrectionReason] = useState('');
  const [correctedLocation, setCorrectedLocation] = useState('');
  const [correctedRoadName, setCorrectedRoadName] = useState('');
  const [correctedCity, setCorrectedCity] = useState('');
  const [correctedGps, setCorrectedGps] = useState('');
  const [locationCorrectionReason, setLocationCorrectionReason] = useState('');
  const [recallMode, setRecallMode] = useState<RecallMode>('default');
  const [recallReason, setRecallReason] = useState('');
  const [recallTemplateId, setRecallTemplateId] = useState('');
  const [recallMessageOverride, setRecallMessageOverride] = useState('');
  const [isNavigatingFine, setIsNavigatingFine] = useState(false);
  const [infractions, setInfractions] = useState<string[]>();
  const { mutateAsync: infractionMutation, isPending: infractionPending } =
    useAddInfraction();
  const { mutateAsync: notifyFineMutation, isPending: isSendingSms } =
    useOfficerNotifyFine();
  const { mutateAsync: acceptFineMutation, isPending: isAcceptingFine } =
    useAcceptCaseCheckerFine();
  const { mutateAsync: rejectFineMutation, isPending: isRejectingFine } =
    useRejectCaseCheckerFine();
  const {
    mutateAsync: correctCaseCheckerFineMutation,
    isPending: isCorrectingCaseCheckerFine,
  } = useCorrectCaseCheckerPlate();
  const { mutateAsync: recallFineMutation, isPending: isRecallingFine } =
    useRecallFine();
  const { data: smsTemplates = [], isLoading: isLoadingSmsTemplates } =
    useGetSmsTemplates({
      enabled: showRecallModal,
    });
  const { fetchNextFine, fetchPreviousFine } = useFines();
  const navigate = useNavigate();

  const client =
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'api-service-client';
  const isNTSAUser =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes('ntsa');
  const isFinanceUser =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes('finance');
  const isCaseChecker =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes(
      'case_checker',
    );
  const canRecallSms =
    isCaseChecker &&
    fine?.smsStatus === 'S' &&
    fine?.enforcementStatus !== 'RECALLED';
  const canTakeCaseCheckerDecision =
    isCaseChecker &&
    ['RECALLED', 'PENDING_CASE_CHECKER_ACTION'].includes(
      fine?.enforcementStatus ?? '',
    );

  const handleNextFine = async () => {
    if (!fine || isNavigatingFine) return;
    setIsNavigatingFine(true);
    try {
      const nextFine = await fetchNextFine(String(fine.id));
      if (nextFine) {
        navigate(`/fines/single/${nextFine.ticketNumber}`);
      } else {
        toast.info('There are no more fines after this one');
      }
    } catch {
      toast.error('There was an error loading the next fine', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        theme: 'light',
        transition: Bounce,
      });
    } finally {
      setIsNavigatingFine(false);
    }
  };

  const handlePreviousFine = async () => {
    if (!fine || isNavigatingFine) return;
    setIsNavigatingFine(true);
    try {
      const previousFine = await fetchPreviousFine(String(fine.id));
      if (previousFine) {
        navigate(`/fines/single/${previousFine.ticketNumber}`);
      } else {
        toast.info('There are no fines before this one');
      }
    } catch {
      toast.error('There was an error loading the previous fine', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        theme: 'light',
        transition: Bounce,
      });
    } finally {
      setIsNavigatingFine(false);
    }
  };

  const sampleOptions = [
    { value: '8', label: 'Driving while using a mobile phone' },
    {
      value: '18',
      label: 'Failure to wear a seat belt while the vehicle is in motion',
    },
    {
      value: '26',
      label: 'Travelling with a part of the body outside a moving vehicle',
    },
  ];

  const handleChange = (selectedValues: string[]) =>
    setInfractions(selectedValues);

  useEffect(() => {
    if (!fine?.id) return;

    let isMounted = true;
    setTrailLog(undefined);
    mutateAsync({ id: fine.id.toString() })
      .then((data) => {
        if (isMounted && data.trailLog) setTrailLog(data.trailLog);
      })
      .catch(() => {
        if (isMounted) setTrailLog(undefined);
      });

    return () => {
      isMounted = false;
    };
  }, [fine?.id, mutateAsync]);

  if (fineLoading || isPending) return <FineDetailsSkeleton />;
  if (!fine)
    return (
      <NoDataHandling content="No fine details found for the specified ID." />
    );

  const handleSendSms = async () => {
    await notifyFineMutation({ fineId: (fine.id ?? '').toString() })
      .then(() => {
        toast.success('Fine notification sent successfully', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
        setShowSmsModal(false);
        navigate('/fines/1');
      })
      .catch(() => {
        toast.error('There was an error sending the fine notification', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
      });
  };

  const handleCaseCheckerActionSuccess = (message: string) => {
    toast.success(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      theme: 'light',
      transition: Bounce,
    });
    navigate('/fines/1');
  };

  const handleAcceptCase = async () => {
    if (!isCaseChecker) return;

    await acceptFineMutation({ fineId: fine.id.toString() })
      .then((response) => {
        setShowAcceptModal(false);
        handleCaseCheckerActionSuccess(response.message);
      })
      .catch(() => {
        toast.error('There was an error accepting this case', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
      });
  };

  const handleRejectCase = async () => {
    if (!isCaseChecker) return;

    const reason = rejectionReason.trim();
    if (!reason) return;

    await rejectFineMutation({ fineId: fine.id.toString(), reason })
      .then((response) => {
        setShowRejectModal(false);
        setRejectionReason('');
        handleCaseCheckerActionSuccess(response.message);
      })
      .catch(() => {
        toast.error('There was an error rejecting this case', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
      });
  };

  const openCorrectPlateModal = () => {
    setCorrectedPlateNumber(fine.vehicleRegistrationNumber ?? '');
    setPlateCorrectionReason('');
    setShowCorrectPlateModal(true);
  };

  const closeCorrectPlateModal = () => {
    setShowCorrectPlateModal(false);
    setCorrectedPlateNumber('');
    setPlateCorrectionReason('');
  };

  const handleCorrectPlate = async () => {
    if (!isCaseChecker) return;

    const vehicleRegistrationNumber = correctedPlateNumber.trim();
    const reason = plateCorrectionReason.trim();
    if (!vehicleRegistrationNumber || !reason) return;

    await correctCaseCheckerFineMutation({
      fineId: fine.id.toString(),
      vehicleRegistrationNumber,
      reason,
    })
      .then((response) => {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
        closeCorrectPlateModal();
      })
      .catch(() => {
        toast.error('There was an error correcting this number plate', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
      });
  };

  const openCorrectLocationModal = () => {
    setCorrectedLocation(fine.location ?? '');
    setCorrectedRoadName(fine.roadName ?? '');
    setCorrectedCity(fine.city ?? '');
    setCorrectedGps(fine.gps ?? '');
    setLocationCorrectionReason('');
    setShowCorrectLocationModal(true);
  };

  const closeCorrectLocationModal = () => {
    setShowCorrectLocationModal(false);
    setCorrectedLocation('');
    setCorrectedRoadName('');
    setCorrectedCity('');
    setCorrectedGps('');
    setLocationCorrectionReason('');
  };

  const handleCorrectLocation = async () => {
    if (!isCaseChecker) return;

    const location = correctedLocation.trim();
    const roadName = correctedRoadName.trim();
    const city = correctedCity.trim();
    const gps = correctedGps.trim();
    const reason = locationCorrectionReason.trim();
    if (!location || !roadName || !city || !gps || !reason) return;

    await correctCaseCheckerFineMutation({
      fineId: fine.id.toString(),
      location,
      roadName,
      city,
      gps,
      reason,
    })
      .then((response) => {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
        closeCorrectLocationModal();
      })
      .catch(() => {
        toast.error('There was an error correcting this location', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
      });
  };

  const resetRecallForm = () => {
    setShowRecallModal(false);
    setRecallMode('default');
    setRecallReason('');
    setRecallTemplateId('');
    setRecallMessageOverride('');
  };

  const handleRecallModeChange = (mode: RecallMode) => {
    setRecallMode(mode);
    setRecallTemplateId('');
    setRecallMessageOverride('');
  };

  const handleRecallSms = async () => {
    if (!canRecallSms) return;

    const reason = recallReason.trim();
    if (!reason) return;
    if (recallMode === 'template' && !recallTemplateId) return;
    if (recallMode === 'override' && !recallMessageOverride.trim()) return;

    const payload = {
      ticketNumber: fine.ticketNumber,
      reason,
      ...(recallMode === 'template'
        ? { smsTemplateId: Number(recallTemplateId) }
        : {}),
      ...(recallMode === 'override'
        ? { smsMessageOverride: recallMessageOverride.trim() }
        : {}),
    };

    await recallFineMutation(payload)
      .then((response) => {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
        resetRecallForm();
        navigate('/fines/1');
      })
      .catch(() => {
        toast.error('There was an error recalling this SMS', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
      });
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    const post = {
      ticketNumber: fine.ticketNumber.toString(),
      vehicleRegistrationNumber: '',
      offences: infractions,
      userId: keycloak?.tokenParsed.sub,
    };

    await infractionMutation(post)
      .then(() =>
        toast.success('The infractions were added successfully', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce,
        }),
      )
      .then(() => setIsOpen(false))
      .catch(() =>
        toast.error('There was an error adding the infractions', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce,
        }),
      );
  };

  return (
    <SingleViewLayout title="Fine Details">
      {showSmsModal && (
        <SmsConfirmModal
          onConfirm={handleSendSms}
          onCancel={() => setShowSmsModal(false)}
          isSending={isSendingSms}
        />
      )}

      {showAcceptModal && (
        <AcceptCaseModal
          onConfirm={handleAcceptCase}
          onCancel={() => setShowAcceptModal(false)}
          isAccepting={isAcceptingFine}
        />
      )}

      {showRejectModal && (
        <RejectCaseModal
          reason={rejectionReason}
          onReasonChange={setRejectionReason}
          onConfirm={handleRejectCase}
          onCancel={() => {
            setShowRejectModal(false);
            setRejectionReason('');
          }}
          isRejecting={isRejectingFine}
        />
      )}

      {showCorrectPlateModal && (
        <CorrectPlateModal
          plateNumber={correctedPlateNumber}
          reason={plateCorrectionReason}
          onPlateNumberChange={setCorrectedPlateNumber}
          onReasonChange={setPlateCorrectionReason}
          onConfirm={handleCorrectPlate}
          onCancel={closeCorrectPlateModal}
          isCorrecting={isCorrectingCaseCheckerFine}
        />
      )}

      {showCorrectLocationModal && (
        <CorrectLocationModal
          location={correctedLocation}
          roadName={correctedRoadName}
          city={correctedCity}
          gps={correctedGps}
          reason={locationCorrectionReason}
          onLocationChange={setCorrectedLocation}
          onRoadNameChange={setCorrectedRoadName}
          onCityChange={setCorrectedCity}
          onGpsChange={setCorrectedGps}
          onReasonChange={setLocationCorrectionReason}
          onConfirm={handleCorrectLocation}
          onCancel={closeCorrectLocationModal}
          isCorrecting={isCorrectingCaseCheckerFine}
        />
      )}

      {showRecallModal && (
        <RecallSmsModal
          mode={recallMode}
          reason={recallReason}
          templateId={recallTemplateId}
          messageOverride={recallMessageOverride}
          smsTemplates={smsTemplates}
          isLoadingTemplates={isLoadingSmsTemplates}
          isRecalling={isRecallingFine}
          onModeChange={handleRecallModeChange}
          onReasonChange={setRecallReason}
          onTemplateChange={setRecallTemplateId}
          onMessageOverrideChange={setRecallMessageOverride}
          onConfirm={handleRecallSms}
          onCancel={resetRecallForm}
        />
      )}

      <div className={`relative ${isNTSAUser ? 'courier' : ''}`}>
        {isNTSAUser && (
          <div className="absolute inset-0 z-10 grid grid-rows-10 grid-cols-5 gap-4 opacity-25 pointer-events-none">
            {Array.from({ length: 50 }).map((_, idx) => (
              <div key={idx} className="flex items-center justify-center">
                <img
                  src="/ntsa-logo.svg"
                  alt="ntsa logo"
                  className="w-24 h-24 object-contain"
                />
              </div>
            ))}
          </div>
        )}

        <div className="relative z-0">
          {/* Top bar: Prev/Next + Send SMS */}
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={handlePreviousFine}
              disabled={isNavigatingFine}
              className="px-4 py-2 bg-gray-200 rounded-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
              <span>{isNavigatingFine ? 'Loading...' : 'Previous'}</span>
            </button>

            {auth.includes('officer') && (
              <button
                disabled={isSendingSms}
                onClick={() => setShowSmsModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
                Send SMS
              </button>
            )}

            <button
              onClick={handleNextFine}
              disabled={isNavigatingFine}
              className="px-4 py-2 bg-gray-200 rounded-lg flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{isNavigatingFine ? 'Loading...' : 'Next'}</span>
              <ChevronRight size={16} />
            </button>
          </div>

          {isOpen && (
            <DialogLayout height="70%" isOpen onClose={() => setIsOpen(false)}>
              <div className="p-6 space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Add Vehicle Infraction
                  </h2>
                  <p className="text-gray-600 text-sm mt-2">
                    Fill out the details below to add a new infraction to a
                    vehicle.
                  </p>
                </div>
                <form className="space-y-6" onSubmit={onSubmit}>
                  <div>
                    <label
                      htmlFor="vehicle-reg"
                      className="block text-lg font-medium text-gray-700 mb-2"
                    >
                      Vehicle Registration Number
                    </label>
                    <input
                      id="vehicle-reg"
                      type="text"
                      disabled
                      value={fine.vehicleRegistrationNumber}
                      placeholder="Enter registration number"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="infractions"
                      className="block text-lg font-medium text-gray-700 mb-2"
                    >
                      Infraction(s) to be Added
                    </label>
                    <MultiSelect
                      options={sampleOptions}
                      onChange={handleChange}
                      placeholder="Select infraction(s)"
                      className="w-full"
                    />
                  </div>
                  <div className="flex justify-center mt-6">
                    <button
                      type="submit"
                      className="px-6 py-3 bg-blue-600 text-white font-medium text-lg rounded-lg shadow-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {infractionPending ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
              </div>
            </DialogLayout>
          )}

          <div className="lg:p-6 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="lg:px-6 px-2 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-semibold">
                  Ticket {fine?.ticketNumber.toString().slice(-6).toUpperCase()}
                </h2>
                {isFinanceUser && (
                  <span
                    className={`px-4 py-1 rounded-full text-sm font-medium ${
                      fine.status === 'PAID'
                        ? 'bg-green-100 text-green-800'
                        : fine.status === 'WARNING'
                          ? 'bg-yellow-500 text-white'
                          : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {fine.status === 'PAID'
                      ? 'Paid'
                      : fine.status === 'WARNING'
                        ? 'Warning'
                        : 'Not paid'}
                  </span>
                )}
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {canTakeCaseCheckerDecision ? (
                    <EditableInfoItem
                      label="Vehicle Registration"
                      value={fine.vehicleRegistrationNumber}
                      onEdit={openCorrectPlateModal}
                      editLabel="Edit vehicle registration"
                      isEditingDisabled={
                        isAcceptingFine ||
                        isRejectingFine ||
                        isCorrectingCaseCheckerFine
                      }
                    />
                  ) : (
                    <InfoItem
                      label="Vehicle Registration"
                      value={fine.vehicleRegistrationNumber}
                    />
                  )}
                  {canTakeCaseCheckerDecision ? (
                    <EditableInfoItem
                      label="Location"
                      value={fine.location}
                      onEdit={openCorrectLocationModal}
                      editLabel="Edit location"
                      isEditingDisabled={
                        isAcceptingFine ||
                        isRejectingFine ||
                        isCorrectingCaseCheckerFine
                      }
                    />
                  ) : (
                    <InfoItem label="Location" value={fine.location} />
                  )}
                  <InfoItem label="Speed Limit" value={fine.speedLimit} />
                  <InfoItem label="Vehicle Speed" value={fine.vehicleSpeed} />
                  <InfoItem
                    label="Exceeded Speed"
                    value={`${fine.vehicleSpeed - fine.speedLimit}`}
                  />
                </div>
              </div>
            </div>

            {/* Infractions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                  <Briefcase className="h-6 w-6 text-gray-600" />
                  <h2 className="text-xl font-bold text-gray-800">
                    Infractions
                  </h2>
                </div>
              </div>
              <div className="p-6">
                {fine.offenses !== undefined &&
                  fine.offenses.length > 0 &&
                  fine.offenses.map((offence) => (
                    <div
                      key={offence.id}
                      className="bg-gray-50 rounded-lg p-4 mb-4 last:mb-0 border border-gray-200"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <InfoItem
                          label="Infraction Time"
                          value={moment(fine.dateCaptured).format(
                            'Do MMMM YYYY h:mm a',
                          )}
                        />
                        {isFinanceUser && (
                          <InfoItem
                            label="Infraction Amount"
                            value={`${offence.amount.toLocaleString()}`}
                          />
                        )}
                        <InfoItem
                          label="Infraction Title"
                          value={offence.offenceName}
                        />
                      </div>
                    </div>
                  ))}
                {isFinanceUser && (
                  <p className="text-right text-lg font-semibold">
                    Total Amount:{' '}
                    {(fine.finesOffenses &&
                    Array.isArray(fine.finesOffenses) &&
                    fine.finesOffenses.length > 0
                      ? fine.finesOffenses.reduce(
                          (sum, item) => sum + item.amount,
                          0,
                        )
                      : 0
                    ).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {/* SMS Details — button removed from here */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex space-x-2 items-center">
                  <MessageCircle className="h-5 w-5 text-gray-500" />
                  <h2 className="text-lg font-semibold">SMS Details</h2>
                </div>
                <div className="flex items-center gap-2">
                  {canRecallSms && (
                    <button
                      disabled={isRecallingFine}
                      onClick={() => setShowRecallModal(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150 border bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 hover:border-orange-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Recall SMS
                    </button>
                  )}
                  <span
                    className={`${fine.smsStatus === 'P' || fine.smsStatus === null ? 'text-black' : 'text-white'} px-3 py-1 rounded-3xl justify-center flex items-center ${getBackgroundColor(fine.smsStatus)}`}
                  >
                    {fine.smsStatus === null
                      ? 'No status'
                      : fine.smsStatus === 'S'
                        ? 'Sent'
                        : fine.smsStatus === 'P'
                          ? 'Pending'
                          : 'Failed'}
                  </span>
                </div>
              </div>
              {!isNTSAUser && (
                <>
                  {trailLog !== undefined ? (
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <InfoItem
                          label="Trail Id"
                          value={trailLog[0].trailId}
                        />
                        <InfoItem
                          label="Trail Time"
                          value={moment(trailLog[0].trailTime).format(
                            'Do MMMM YYYY h:mm a',
                          )}
                        />
                        <InfoItem
                          label="Trail Comment"
                          value={trailLog[0].comment}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 text-center text-gray-500">
                      No trail for this case was found
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Owner Details */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Car className="h-5 w-5 text-gray-500" />
                  <h2 className="text-lg font-semibold">Owner Details</h2>
                </div>
              </div>
              {fine.ownerDetails === null ? (
                <div className="p-6">Owner details not found</div>
              ) : (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InfoItem
                      label="Name"
                      value={`${fine.ownerDetails.name} ${fine.ownerDetails.lastName}`}
                    />
                    <InfoItem
                      label="Phone Number"
                      value={fine.ownerDetails.phoneNumber}
                    />
                  </div>
                </div>
              )}
            </div>

            <ZoomableImage image={fine.imageUrl} alt="fine image" maxZoom={4} />

            {canTakeCaseCheckerDecision && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">
                      Case Decision
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      Review the vehicle registration, location, and violation
                      image before accepting or rejecting this case.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      disabled={
                        isAcceptingFine ||
                        isRejectingFine ||
                        isCorrectingCaseCheckerFine
                      }
                      onClick={() => setShowAcceptModal(true)}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 border bg-green-50 text-green-700 border-green-200 hover:bg-green-100 hover:border-green-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Accept Case
                    </button>
                    <button
                      disabled={
                        isAcceptingFine ||
                        isRejectingFine ||
                        isCorrectingCaseCheckerFine
                      }
                      onClick={() => setShowRejectModal(true)}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 border bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject Case
                    </button>
                  </div>
                </div>
              </div>
            )}

            {auth.includes('case processing accessor') && (
              <button
                onClick={() => setIsOpen(true)}
                className="px-6 text-white py-2 flex space-x-4 items-center bg-blue-500 rounded-lg transition-colors"
              >
                <Plus size={16} />
                Add an additional infraction
              </button>
            )}
          </div>
        </div>
      </div>
    </SingleViewLayout>
  );
};

export default FineDetails;
